import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { KpiMetricsOutput } from '@/types'
import { AccordionBarChart } from './shared/accordion-bar-chart'
import { BarListRow } from './shared/bar-list-row'
import { WordCloud } from './shared/word-cloud'
import { YellowCardIcon } from './shared/yellow-card-icon'

export function CartaoAmareloDashboard({ data }: { data: KpiMetricsOutput }) {
	const { t } = useTranslation()
	const { cartaoAmarelo } = data

	const total = useMemo(
		() => cartaoAmarelo.byBranch.reduce((sum, b) => sum + b.count, 0),
		[cartaoAmarelo],
	)

	const maxContract = Math.max(1, ...cartaoAmarelo.byCr.map((c) => c.count))

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
				<div className="flex items-center gap-4 rounded-lg border border-kaefer-light-silver bg-white p-4">
					<YellowCardIcon className="h-14 w-10 shrink-0 -rotate-12" />
					<div>
						<p className="text-4xl font-extrabold text-kaefer-dark-grey">
							{total}
						</p>
						<p className="text-sm font-medium text-muted-foreground">
							{t('kpiMetrics.cartaoAmarelo.total')}
						</p>
					</div>
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.cartaoAmarelo.words')}
					</span>
					{cartaoAmarelo.words.length === 0 ? (
						<EmptyState t={t} />
					) : (
						<div className="mt-3">
							<WordCloud words={cartaoAmarelo.words} />
						</div>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.cartaoAmarelo.byRisk')}
					</span>
					<div className="mt-1 max-h-72 overflow-y-auto pr-1">
						<AccordionBarChart
							items={cartaoAmarelo.byRisk.map((kind) => ({
								id: kind.kindId,
								label: kind.kindName,
								value: kind.count,
								children: kind.risks,
							}))}
							getChildId={(risk) => risk.riskId}
							getChildLabel={(risk) => risk.riskName}
							getChildValue={(risk) => risk.count}
							colorClass="bg-kaefer-red"
							childColorClass="bg-kaefer-red/50"
							emptyState={<EmptyState t={t} />}
						/>
					</div>
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.cartaoAmarelo.byBranch')}
					</span>
					<div className="mt-1 max-h-72 overflow-y-auto pr-1">
						<AccordionBarChart
							items={cartaoAmarelo.byBranch.map((b) => ({
								id: b.branchId,
								label: b.branchName,
								value: b.count,
								children: b.crs,
							}))}
							getChildId={(cr) => cr.crId}
							getChildLabel={(cr) => cr.crName}
							getChildValue={(cr) => cr.count}
							colorClass="bg-kaefer-honey-yellow"
							childColorClass="bg-kaefer-medium-grey"
							emptyState={<EmptyState t={t} />}
						/>
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.cartaoAmarelo.efetivo')}
					</span>
					{cartaoAmarelo.efetivo.length === 0 ? (
						<EmptyState t={t} />
					) : (
						<div className="mt-3 flex max-h-72 flex-col gap-2.5 overflow-y-auto pr-1">
							{cartaoAmarelo.efetivo.map((e) => (
								<BarListRow
									key={e.branchId}
									label={e.branchName}
									displayValue={(e.ratePct / 100).toFixed(2)}
									widthPct={Math.min(e.ratePct, 100)}
									colorClass="bg-kaefer-honey-yellow"
									tooltip={
										<div className="flex flex-col gap-0.5">
											<span>
												{t('kpiMetrics.cartaoAmarelo.efetivoTooltipCartoes', {
													cartoes: e.cartoes,
												})}
											</span>
											<span>
												{t('kpiMetrics.cartaoAmarelo.efetivoTooltipEfetivo', {
													efetivo: e.efetivo,
												})}
											</span>
											<span>
												{t('kpiMetrics.cartaoAmarelo.efetivoTooltipRate', {
													ratePct: (e.ratePct / 100).toFixed(2),
												})}
											</span>
										</div>
									}
								/>
							))}
						</div>
					)}
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.cartaoAmarelo.byCr')}
					</span>
					{cartaoAmarelo.byCr.length === 0 ? (
						<EmptyState t={t} />
					) : (
						<div className="mt-3 flex max-h-72 flex-col gap-2.5 overflow-y-auto pr-1">
							{cartaoAmarelo.byCr.map((c, index) => (
								<BarListRow
									key={c.crId ?? `empty-${index}`}
									label={c.crName}
									displayValue={String(c.count)}
									widthPct={(c.count / maxContract) * 100}
									colorClass="bg-kaefer-medium-grey"
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

function EmptyState({ t }: { t: (key: string) => string }) {
	return (
		<p className="py-10 text-center text-sm text-muted-foreground">
			{t('kpiMetrics.charts.noData')}
		</p>
	)
}
